import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaCheckCircle,
  FaUsers,
  FaGraduationCap,
  FaChartLine,
  FaSchool,
  FaApple,
  FaGooglePlay,
  FaMobileAlt
} from "react-icons/fa";
import "../../styles/app-download.css";

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const slides = [
    {
      id: 1,
      title: "India's Complete School & Coaching Management System",
      subtitle: "Simplify Administration with Our All-in-One Learning Management Software",
      description:
        "Join thousands of educational institutions using Pathshala Sanchalak to streamline operations. Our comprehensive school and coaching management system helps you manage students, faculty, and administrative tasks with ease.",
      primaryBtn: "Explore School Management Features",
      secondaryBtn: "Coaching Management Solutions",
      primaryLink: "/school-management-system",
      secondaryLink: "/coaching-management-system",
      icon: FaSchool,
      backgroundImage:
        "",
    },
    {
      id: 2,
      title: "Smart Student Management System",
      subtitle: "Track Progress, Attendance & Performance",
      description:
        "Comprehensive student profiles with real-time attendance tracking, grade management, and progress analytics. Keep parents informed with automated notifications and detailed reports.",
      primaryBtn: "Student Features",
      secondaryBtn: "View Demo",
      primaryLink: "#services",
      secondaryLink: "#contact",
      icon: FaUsers,
      backgroundImage:
        "",
    },
    {
      id: 3,
      title: "Faculty Empowerment Platform",
      subtitle: "Simplify Teaching & Administrative Tasks",
      description:
        "Advanced tools for lesson planning, assignment management, grade calculation, and student communication. Empower your teachers with technology that enhances their teaching experience.",
      primaryBtn: "Faculty Tools",
      secondaryBtn: "Get Started",
      primaryLink: "#services",
      secondaryLink: "#contact",
      icon: FaGraduationCap,
      backgroundImage:
        "",
    },
    {
      id: 4,
      title: "Advanced Analytics & Reporting",
      subtitle: "Data-Driven Educational Insights",
      description:
        "Comprehensive dashboards with detailed analytics on student performance, attendance trends, financial reports, and institutional metrics. Make informed decisions with powerful data visualization.",
      primaryBtn: "View Analytics",
      secondaryBtn: "Learn More",
      primaryLink: "#services",
      secondaryLink: "#about",
      icon: FaChartLine,
      backgroundImage:
        "",
    },
    {
      id: 5,
      title: "Mobile Apps for Students and Faculty",
      subtitle: "Access Education Anytime, Anywhere",
      description:
        "Enhance learning and teaching with our dedicated mobile applications. Students and faculty can access resources, track progress, and communicate seamlessly from their mobile devices.",
      primaryBtn: "Student App",
      secondaryBtn: "Faculty App",
      primaryLink: "https://play.google.com/store/apps/details?id=com.bojack.chhaatrpathshalasanchalakapp&pcampaignid=web_share",
      secondaryLink: "https://play.google.com/store/apps/details?id=com.bojack.adhyapakpathshalasanchalakapp&pcampaignid=web_share",
      icon: FaMobileAlt,
      backgroundImage:
        "",
      hasAppLinks: true,
    },
  ];

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setIsAnimating(false);
      }, 300);
    }, 10000); // Change slide every 10 seconds

    return () => clearInterval(interval);
  }, [slides.length]);

  const goToSlide = (index) => {
    if (index !== currentSlide && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setIsAnimating(false);
      }, 300);
    }
  };

  const currentSlideData = slides[currentSlide];
  const IconComponent = currentSlideData.icon;

  return (
    <section id="home" className="hero">
      <div
        className="hero-background"
        style={{
          backgroundImage: `linear-gradient(rgba(44, 62, 80, 0.8), rgba(44, 62, 80, 0.8)), url(${currentSlideData.backgroundImage})`,
        }}
      ></div>
      <div className="hero-overlay"></div>

      <div className="container">
        <div className={`hero-content ${isAnimating ? "fade-out" : "fade-in"}`}>
          <div className="hero-icon">
            <IconComponent />
          </div>
          <h1>{currentSlideData.title}</h1>
          <div className="free-badge-container">
            <div className="free-badge">
              <FaCheckCircle className="check-icon" />
              <span>{currentSlideData.subtitle}</span>
            </div>
          </div>
          <p>{currentSlideData.description}</p>
          <div className="hero-buttons">
            {currentSlideData.hasAppLinks ? (
              <div className="app-store-links-container">
                <div className="app-store-links hero-app-links">
                  <a 
                    href="https://apps.apple.com/in/app/ps-chhaatr/id6745966365" 
                    className="app-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Chhaatr App on App Store"
                  >
                    <FaApple /> iOS Chhaatr App
                  </a>
                  <a 
                    href={currentSlideData.primaryLink}
                    className="play-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Chhaatr App on Google Play"
                  >
                    <FaGooglePlay /> Android Chhaatr App
                  </a>
                </div>
                <div className="app-store-links hero-app-links">
                  <a 
                    href="https://apps.apple.com/in/app/ps-adhyapak/id6745964284" 
                    className="app-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Adhyapak App on App Store"
                  >
                    <FaApple /> iOS Adhyapak App
                  </a>
                  <a 
                    href={currentSlideData.secondaryLink}
                    className="play-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Adhyapak App on Google Play"
                  >
                    <FaGooglePlay /> Android Adhyapak App
                  </a>
                </div>
              </div>
            ) : (
              <>
                {currentSlideData.primaryLink.startsWith('/') ? (
                  <Link to={currentSlideData.primaryLink} style={{gap:"10px"}} className="btn btn-primary">
                    {currentSlideData.primaryBtn} {" "}<FaArrowRight className="icon" />
                  </Link>
                ) : (
                  <a href={currentSlideData.primaryLink} style={{gap:"10px"}} className="btn btn-primary">
                    {currentSlideData.primaryBtn} {" "} <FaArrowRight className="icon" />
                  </a>
                )}
                {currentSlideData.secondaryLink.startsWith('/') ? (
                  <Link to={currentSlideData.secondaryLink} style={{gap:"10px"}} className="btn btn-outline">
                    {currentSlideData.secondaryBtn} {" "} <FaArrowRight className="icon" />
                  </Link>
                ) : (
                  <a href={currentSlideData.secondaryLink} style={{gap:"10px"}} className="btn btn-outline">
                    {currentSlideData.secondaryBtn} {" "} <FaArrowRight className="icon" />
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="hero-indicators">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`indicator ${index === currentSlide ? "active" : ""}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="hero-progress">
        <div
          className="progress-bar"
          style={{
            animationDuration: "10s",
            animationIterationCount: "infinite",
            animationTimingFunction: "linear",
          }}
        ></div>
      </div>
    </section>
  );
};

export default Hero;
